// Centralised content so pages stay focused on layout.

export const CALENDLY_URL = "https://cal.com/pascale-lebaillif/30min";

export const IMG = {
  hero: "https://customer-assets.emergentagent.com/job_influence-lead/artifacts/plnbj4gv_Screenshot%202026-04-27%20at%2021.25.43.png",
  navy: "https://customer-assets.emergentagent.com/job_influence-lead/artifacts/p2we8g7b_008-9tKljCWLpss.jpeg",
  navyAlt: "https://customer-assets.emergentagent.com/job_influence-lead/artifacts/8xoslbsl_007-woBk620DgsU.jpeg",
  cutout: "https://customer-assets.emergentagent.com/job_influence-lead/artifacts/yc0p4g3u_Paschalia-152%20-%20no%20BGD.png",
};

export const CLIENTS = [
  "BlackRock",
  "BCG Platinion",
  "ESSEC Business School",
  "Taylor Root",
  "UK Chinese Women Connect",
];

export const PROBLEMS = [
  {
    quote: "We promote experts who struggle as leaders.",
    note: "Technical excellence does not translate into senior leadership effectiveness.",
  },
  {
    quote: "We are losing high-potential women at VP and Director level.",
    note: "The pipeline tightens precisely where the business needs it most.",
  },
  {
    quote: "Our leadership programmes are not translating into behaviour change.",
    note: "Generic content, low transfer to the day-to-day reality of the role.",
  },
  {
    quote: "We have diversity ambitions, but not enough women ready for senior roles.",
    note: "Targets without a credible pathway to deliver them.",
  },
];

export const WHY = {
  eyebrow: "Why this work",
  headline: "I've sat on the committees where careers are decided. I know exactly what differentiates the women who step into leadership from the ones who remain valued for delivery.",
  body: [
    "Twenty years inside Morgan Stanley, Credit Suisse and J.P. Morgan, leading global teams and sitting on the talent and promotion committees, deciding senior progression. The view from that side of the table is unambiguous: brilliance is not what gets you promoted at senior level. Visibility, political intelligence, and the ability to lead through other people is.",
    "I have watched ready, exceptional women get passed over, not because they could not do the job, but because no one had ever shown them how the senior game was actually being played. I know what that costs the individual. I also know what it costs the business: a thin pipeline, repeated promotion misfires, and senior roles filled by default rather than design.",
    "That is the work I do. Turning high-performing women into visible, influential leaders before organisations lose them, overlook them, or underestimate them.",
  ],
  signature: "Pascale Lebaillif",
};

export const METHOD = {
  eyebrow: "The method",
  headline: "What got you here won't get you there.",
  intro: "Many senior women are still operating with the mindset that got them promoted ten years ago. Deliver more. Be indispensable. Stay across everything.\n\nBut leadership at the next level is not about proving value through output. It is about influence, positioning, and strategic authority.",
  steps: [
    { n: "01", title: "See the cage.",     body: "Identify hidden blockers to leadership effectiveness." },
    { n: "02", title: "Shift the mindset.", body: "Move from execution to decision-making authority." },
    { n: "03", title: "Install the tools.", body: "Build influence, visibility, and strategic positioning." },
    { n: "04", title: "Anchor the shift.",  body: "Ensure behavioural change under real business pressure." },
  ],
  closing: "Two decades leading at senior level globally, combined with transformational coaching expertise. Real-world leadership experience paired with the psychological and strategic tools that create measurable change.",
};

export const PROGRAMMES = [
  {
    slug: "expert-to-leader",
    eyebrow: "Cohort Programme",
    title: "Leadership Transition Programme",
    subtitle: "For women transitioning from high performance into strategic leadership.",
    lede: "A multi-week leadership transition programme for senior women moving beyond technical excellence and operational delivery into influence, visibility, and enterprise-level leadership.",
    midItalic: "Because the skills that create exceptional performers are not the same skills that create influential leaders.",
    headlineOutcome: "Promotion readiness, executive presence, strategic influence, and leadership authority within 3–6 months.",
    bodyParagraphs: [
      "The issue is rarely the quality of these women's thinking. Many are already among the strongest operators in the organisation. The problem is that their influence often does not match the value they bring.",
      "When high-performing women strengthen visibility, strategic positioning, and stakeholder management, organisations benefit from stronger succession pipelines, broader leadership contribution, and better strategic decision-making.",
      "This isn't a leadership development programme. It's a practitioner-led transition programme, designed by a senior leader who has made these moves herself and enabled other women to do the same. The lens here is not HR. It is frontline corporate reality.",
      "These sessions are led by Pascale, a former senior leader in global financial organisations who has navigated these transitions herself and built this programme from lived corporate experience, not theory.",
    ],
    format: "8–10 participants · Multi-week · London or hybrid",
    outcomes: [
      "Read and navigate organisational power structures with greater confidence.",
      "Shift from execution and delivery into strategic leadership influence.",
      "Build visibility, credibility, and leadership presence at senior level.",
      "Strengthen stakeholder management and upward influence.",
      "Manage the psychological transition from expert identity to leadership identity.",
      "Increase strategic contribution and influence across the organisation.",
    ],
    sessions: [
      "Facilitated leadership working sessions led by Pascale.",
      "Applied leadership challenges between sessions.",
      "Peer accountability and strategic discussion inside the cohort.",
      "Sponsor check-ins aligned with business outcomes.",
      "London or hybrid delivery format.",
    ],
    fit: "Best suited for organisations promoting or preparing senior women for broader leadership responsibility, where the gap is not capability, but influence, visibility, strategic authority, and organisational navigation.",
  },
  {
    slug: "leadership-coaching",
    eyebrow: "Executive Coaching",
    title: "1-2-1 Leadership Coaching",
    subtitle: "Confidential, strategic coaching for women operating at or approaching senior leadership level.",
    lede: "Ongoing executive coaching grounded in the realities of corporate power, promotion, visibility, stakeholder management, and leadership transition.",
    midItalic: "For women moving from trusted executor to influential leader.",
    headlineOutcome: "Strategic authority. Composed leadership presence. Stronger decision-making under pressure.",
    bodyParagraphs: [
      "One-to-one leadership coaching with Pascale isn't just guided reflection. It is informed by two decades of senior leadership inside organisations of the highest calibre, including the decisions, transitions, politics, and moments of uncertainty that no programme fully prepares you for.",
      "Many of these women already possess the judgment, resilience, and commercial intelligence organisations need at senior level. But when exceptional talent remains trapped in execution rather than influence, businesses underutilise some of their strongest leadership capability.",
      "As visibility, stakeholder management, and strategic influence strengthen, organisations benefit from broader leadership contribution, stronger collaboration, and better decision-making at senior level.",
    ],
    format: "6 × 60-minute sessions per quarter · 1 stakeholder check-in · Minimum 3 coachees per organisation",
    outcomes: [
      "Greater influence across senior stakeholders and decision-makers.",
      "Stronger political intelligence and organisational navigation.",
      "The ability to lead through others rather than carrying execution personally.",
      "More visible, credible leadership presence under pressure.",
      "A confidential strategic space to think beyond day-to-day delivery.",
      "Increased ability to shape decisions, not simply execute them.",
    ],
    sessions: [
      "Quarterly executive coaching engagement.",
      "Sponsor-aligned leadership objectives.",
      "Executive-level confidentiality and discretion.",
      "Optional stakeholder and 360 input.",
      "Tailored to promotion, scope expansion, or executive transition.",
    ],
    fit: "For organisations investing in high-performing women at moments of increased visibility, promotion, succession, or leadership transition, and unwilling to leave the outcome to chance.",
  },
  {
    slug: "atlantis",
    eyebrow: "Strategic Leadership Simulation",
    title: "Atlantis",
    subtitle: "A high-stakes leadership simulation for senior teams and leadership cohorts.",
    lede: "Atlantis is a highly advanced civilisation beginning to fracture under pressure. Political tensions are rising. Critical systems are unstable. Trust in leadership is eroding. Different factions disagree on what should happen next.",
    bodyParagraphs: [
      "Participants become members of the High Council of Atlantis. Each governs a key domain of the civilisation: Energy, Infrastructure, Public Stability, Trade, Innovation, Defence, Knowledge, Resources, and Diplomacy.",
      "Throughout the day, the Council faces escalating crises, incomplete information, competing priorities, hidden agendas, ethical dilemmas, and decisions with consequences that compound.",
      "What unfolds is not a workshop. It is a behavioural laboratory. Who becomes influential. Who self-silences. Who creates trust. Who dominates. Who avoids conflict. Who thinks strategically. Who over-functions operationally. How alliances form. How fear shapes decisions. How truth becomes difficult to express.",
      "Most organisations do not fail from lack of intelligence. They fail because of what happens between people when uncertainty rises. Atlantis surfaces those patterns in real time, with the stakes feeling real enough to remove the usual filters.",
      "The result is a precise, observable picture of how your leaders actually operate when the script disappears. The kind of picture interviews, personality assessments, and traditional development programmes consistently fail to produce.",
    ],
    emphasis: [
      "Reveal real leadership behaviour.",
      "Map how power moves inside your teams.",
      "Strengthen judgement, influence, and trust when certainty disappears.",
    ],
    format: "15–30 participants · Full day · Facilitated debrief",
    outcomes: [
      "Greater awareness of how they actually operate under pressure.",
      "Stronger decision-making when information is incomplete.",
      "Clearer sense of how they influence, challenge, and align with others.",
      "Sharper instincts for political and stakeholder dynamics.",
      "A working language for trust, power, and group behaviour inside leadership teams.",
      "A more honest view of their own leadership presence and blind spots.",
    ],
    organisationsGain: [
      "Live, real-time insight into how leaders behave when stakes feel real.",
      "A clearer view of how pressure, hierarchy, communication patterns, and workplace culture influence the way teams operate and make decisions.",
      "Early visibility of leadership potential, behavioural risks, and hidden patterns inside the team.",
      "A more accurate read on leadership readiness than interviews or assessments can produce.",
      "Stronger collective judgement and alignment across the senior team.",
      "A shared vocabulary for the dynamics that shape every leadership conversation.",
    ],
    sessions: [
      "Council formation, with each participant taking a governing domain.",
      "A sequence of escalating crises, decisions, and ethical dilemmas.",
      "Hidden information, competing priorities, and shifting alliances throughout.",
      "Live behavioural observation by facilitators across the day.",
      "Structured debrief linking observed behaviour to the realities of your own leadership context.",
    ],
    fit: "For organisations that want to see how leadership actually operates inside their team, not how it is described on a slide. Best suited to senior leadership groups, high-potential cohorts, succession candidates, executive offsites, and integration moments where alignment under pressure matters.",
    postSession: {
      heading: "What happens after the day?",
      paragraphs: [
        "Atlantis does not end when the simulation ends.",
        "The session surfaces angles, validates instincts, and identifies blind spots that standard assessments and leadership programmes rarely find. After the behavioural laboratory, we anchor those insights through a structured debrief, helping organisations and participants translate what emerged in the room into clearer leadership behaviours, talent conversations, and next-step development priorities.",
        "It gives your organisation a more accurate read on who your leaders truly are under pressure, and a shared vocabulary to act on it.",
      ],
    },
    businessCase: "Most organisations have already run the assessments, the interviews, and the performance reviews. Atlantis complements and stress-tests that picture. It shows you how your high-potential leaders actually behave when the stakes are real, not how they say they do.",
    practitionerNote: "Atlantis is designed and facilitated by Pascale, drawing on two decades inside senior corporate leadership and the patterns she has watched play out, repeatedly, inside real teams.",
  },
];

export const TESTIMONIALS = [
  {
    pull: "I am now in active promotion conversations with leadership.",
    quote:
      "I reached out to Pascale feeling completely overwhelmed by a period of company change. She helped me gain clarity and perspective by asking insightful questions that truly helped me see the path ahead. I came out of each session with a concrete steps plan which I went on to implement. The direct result is that I'm not only successfully navigating the transition, I'm also in active conversations with my bosses about a promotion.",
    author: "E.",
    role: "Senior Finance Lawyer",
  },
  {
    pull: "I gained visibility at senior level within months.",
    quote:
      "Pascale immediately identified the right tools to help me overcome the stumbling blocks I was facing at work. Her in-depth knowledge of both the French and English professional environments and her experience in the financial sector in London allowed her to give very precise advice on how to approach sensitive topics with senior managers. She helped me gain visibility within my company.",
    author: "Helena",
    role: "Head of Legal",
  },
  {
    pull: "Built C-suite support to drive culture change.",
    quote:
      "Pascale helped me develop a strategy to garner support from the C-suite and other colleagues in key roles to implement the changes needed to create new capabilities and drive culture change inside the organisation. Working with Pascale gave me the confidence and skills to become more resilient, clearer in my priorities, and more effective overall.",
    author: "Maria",
    role: "Communications Manager",
  },
  {
    pull: "Directly transformed my career trajectory.",
    quote:
      "Her professional background, coupled with actionable insights and a laser focus on achieving fast results, has directly transformed my career trajectory. Pascale's methodical, structured approach, including follow-ups and tasks between sessions, is highly effective. There is no one better placed to help you test your perspective, shape your strategy, and navigate your next career step.",
    author: "Annette",
    role: "Senior Legal Counsel · M&A, PE, Equity Finance",
  },
];

export const STATS = [
  { value: "20+", label: "Years in global finance & law" },
  { value: "8–10", label: "Participants per cohort" },
  { value: "3–6", label: "Months to visible impact" },
  { value: "3", label: "Tier-one investment banks" },
];
